/**
 * PreviewRenderer - Material preview generation system
 *
 * Renders material previews using a manually-managed RenderTargetTexture with
 * an isolated renderList and camera. This avoids the scene-wide side-effects
 * of CreateScreenshotUsingRenderTargetAsync (which overrides engine
 * getRenderWidth/Height, fires onResizeObservable, swaps the active camera,
 * and calls scene.render() — all of which cause the selection outline to
 * flash).
 *
 * A reusable sphere + camera + RTT rig avoids repeated scene-graph mutations.
 * Previews are serialized through a queue to prevent interference.
 */

// Direct imports for optimal tree shaking
import { Scene } from '@babylonjs/core/scene';
import { Material } from '@babylonjs/core/Materials/material';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { RenderTargetTexture } from '@babylonjs/core/Materials/Textures/renderTargetTexture';
import { Constants } from '@babylonjs/core/Engines/constants';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import type { Light } from '@babylonjs/core/Lights/light';
import { Logger } from '../utils/Logger';
import { waitForRttReady } from './ThumbnailCapture';

const DEFAULT_PREVIEW_SIZE = 128;
const SPHERE_OFFSET = new Vector3(10000, 10000, 10000);

interface MaterialCacheKey {
  materialId: string;
  albedoColor?: string;
  textureUrl?: string;
  uvScale?: number;
  size?: number;
}

/** Reusable preview objects that live in the main scene but are invisible to normal rendering. */
interface PreviewRig {
  sphere: Mesh;
  camera: ArcRotateCamera;
  light: Light;
  rtt: RenderTargetTexture;
  scene: Scene;
}

export class PreviewRenderer {
  private previewQueue: Promise<void> = Promise.resolve();
  private previewCache: Map<string, string> = new Map();
  private cacheHits: number = 0;
  private cacheMisses: number = 0;

  /** Lazy-created rig, reused across the queue. */
  private rig: PreviewRig | null = null;

  /** Reusable canvas for pixel → PNG conversion. */
  private _convertCanvas: HTMLCanvasElement | null = null;

  /** Preview render resolution (width and height). */
  private _size: number = DEFAULT_PREVIEW_SIZE;

  /**
   * Generate a cache key for a material based on its visual properties
   */
  private generateMaterialCacheKey(material: Material, objectName?: string, slotName?: string, size?: number): string {
    const key: MaterialCacheKey = {
      materialId: material.uniqueId?.toString() || material.name || 'unknown',
      size,
    };

    if (material instanceof PBRMaterial && material.albedoColor) {
      key.albedoColor = material.albedoColor.toHexString();
    } else if (material instanceof StandardMaterial && material.diffuseColor) {
      key.albedoColor = material.diffuseColor.toHexString();
    }

    const mainTexture = material instanceof PBRMaterial ? material.albedoTexture
      : material instanceof StandardMaterial ? material.diffuseTexture
      : null;
    if (mainTexture) {
      const internalTex = mainTexture.getInternalTexture();
      if (internalTex) {
        key.textureUrl = internalTex.url;
      }
    }

    if (mainTexture instanceof Texture) {
      key.uvScale = mainTexture.uScale;
    }

    return JSON.stringify({
      ...key,
      objectName: objectName || '',
      slotName: slotName || '',
    });
  }

  /**
   * Ensure the reusable preview rig exists for the given scene and size.
   * Recreates the rig if the scene changed, size changed, or the RTT's
   * internal texture was disposed (e.g. by a scene clear during "open").
   */
  private ensureRig(mainScene: Scene, size: number): PreviewRig {
    if (this.rig && this.rig.scene === mainScene && this.rig.rtt.getInternalTexture()
        && this._size === size) {
      return this.rig;
    }
    this._size = size;

    // Dispose stale rig if scene/size changed
    this.disposeRig();

    const sphere = MeshBuilder.CreateSphere(
      '__preview_sphere__',
      { segments: 32, diameter: 0.5 },
      mainScene
    );
    sphere.position = SPHERE_OFFSET;
    sphere.isPickable = false;

    const camera = new ArcRotateCamera(
      '__preview_cam__',
      -Math.PI / 4,
      Math.PI / 3,
      0.75,
      SPHERE_OFFSET,
      mainScene
    );
    camera.minZ = 0.1;

    // Dedicated light so previews look correct regardless of scene lighting
    const light = new HemisphericLight(
      '__preview_light__',
      new Vector3(0.3, 1, 0.5),
      mainScene
    );
    light.intensity = 1.2;
    light.includedOnlyMeshes = [sphere];

    const rtt = new RenderTargetTexture(
      '__preview_rtt__',
      { width: this._size, height: this._size },
      mainScene,
      false, // generateMipMaps
      false, // doNotChangeAspectRatio — false so the RTT recalculates camera projection for its square aspect ratio
      Constants.TEXTURETYPE_UNSIGNED_BYTE, // force UNSIGNED_BYTE so readPixels returns Uint8Array
    );
    rtt.activeCamera = camera;
    // Clear to transparent instead of inheriting the scene background. Babylon
    // clears an RTT with `rtt.clearColor ?? scene.clearColor`, so without this a
    // user-set environment background color bleeds into every material preview.
    rtt.clearColor = new Color4(0, 0, 0, 0);
    rtt.renderList = [sphere];
    rtt.renderParticles = false;
    rtt.renderSprites = false;
    // Render to the full texture, ignoring the camera's viewport aspect ratio
    rtt.ignoreCameraViewport = true;
    // Don't skip based on layer mask — we want our sphere to always render
    rtt.forceLayerMaskCheck = false;

    this.rig = { sphere, camera, light, rtt, scene: mainScene };
    return this.rig;
  }

  /** Dispose the reusable rig. */
  private disposeRig(): void {
    if (!this.rig) return;
    this.rig.rtt.dispose();
    this.rig.light.dispose();
    this.rig.sphere.dispose();
    this.rig.camera.dispose();
    this.rig = null;
  }

  /**
   * Convert RGBA pixel buffer (bottom-up) to a PNG data URL.
   * Uses a plain HTMLCanvasElement to avoid DPR / WebGL context issues.
   */
  private pixelsToDataUrl(pixels: ArrayBufferView, width: number, height: number): string {
    if (!this._convertCanvas) {
      this._convertCanvas = document.createElement('canvas');
    }
    const canvas = this._convertCanvas;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;

    // Normalise to Uint8ClampedArray regardless of source buffer type
    let src: Uint8ClampedArray;
    if (pixels instanceof Float32Array) {
      // Convert 0–1 floats to 0–255
      const u8 = new Uint8ClampedArray(pixels.length);
      for (let i = 0; i < pixels.length; i++) {
        u8[i] = Math.round(Math.min(1, Math.max(0, pixels[i])) * 255);
      }
      src = u8;
    } else {
      src = new Uint8ClampedArray(pixels.buffer, pixels.byteOffset, pixels.byteLength);
    }

    // RTT readPixels returns bottom-up rows — flip vertically
    const imageData = ctx.createImageData(width, height);
    const dst = imageData.data;
    const rowBytes = width * 4;
    for (let y = 0; y < height; y++) {
      const srcOffset = (height - 1 - y) * rowBytes;
      const dstOffset = y * rowBytes;
      dst.set(src.subarray(srcOffset, srcOffset + rowBytes), dstOffset);
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL('image/png');
  }

  /**
   * Generate a preview image for a material.
   *
   * Uses a dedicated RenderTargetTexture with its own camera and render list.
   * The RTT is rendered manually via rtt.render(), which does NOT touch the
   * scene's active camera, does NOT override engine dimensions, and does NOT
   * fire onResizeObservable — so the selection outline layer is undisturbed.
   *
   * `cacheKeyOverride` replaces the identity the cache is keyed on. The derived
   * key includes `material.uniqueId`, which is fine for a material that lives
   * as long as its mesh but useless for a THROWAWAY clone — every call would
   * miss and then leave a full-size data URL behind under a key nothing can hit
   * again. A caller that renders ephemeral clones of a stable source (the
   * material-asset path) passes its own key so those writes overwrite rather
   * than accumulate.
   */
  async renderMaterialPreview(
    material: Material,
    mainScene: Scene,
    objectName?: string,
    slotName?: string,
    forceRegenerate: boolean = false,
    size: number = DEFAULT_PREVIEW_SIZE,
    cacheKeyOverride?: string,
  ): Promise<string> {
    const cacheKey = cacheKeyOverride
      ?? this.generateMaterialCacheKey(material, objectName, slotName, size);

    if (!forceRegenerate && this.previewCache.has(cacheKey)) {
      this.cacheHits++;
      Logger.debug(`[PreviewRenderer] Cache hit for material ${material.name || 'unknown'} (${this.cacheHits} hits, ${this.cacheMisses} misses)`);
      return this.previewCache.get(cacheKey)!;
    }

    this.cacheMisses++;

    return new Promise((resolve) => {
      this.previewQueue = this.previewQueue.then(async () => {
        try {
          const rig = this.ensureRig(mainScene, size);

          // Swap material on the reusable sphere
          rig.sphere.material = material;

          // Wait for the RTT to be renderable (material shaders compiled,
          // textures loaded, vertex buffers uploaded). See `waitForRttReady`
          // in ThumbnailCapture.ts for the rationale and history of approaches.
          await waitForRttReady(rig.rtt, mainScene, `preview:${material.name || 'unknown'}`);

          // Render to the offscreen RTT
          rig.rtt.render();

          // Read back the rendered pixels
          const pixelsPromise = rig.rtt.readPixels();
          const pixels = pixelsPromise ? await pixelsPromise : null;

          if (!pixels) {
            Logger.warn(`[PreviewRenderer] readPixels returned null for material ${material.name || 'unknown'}`);
            resolve('');
            return;
          }

          // Convert to PNG data URL
          const dataUrl = this.pixelsToDataUrl(pixels, this._size, this._size);

          if (dataUrl && dataUrl.length > 0) {
            this.previewCache.set(cacheKey, dataUrl);
            Logger.debug(`[PreviewRenderer] Preview generated and cached for material ${material.name || 'unknown'} (${this.cacheHits} hits, ${this.cacheMisses} misses)`);
          } else {
            Logger.warn(`[PreviewRenderer] Empty preview for material ${material.name || 'unknown'}, not caching`);
          }

          resolve(dataUrl);
        } catch (e) {
          Logger.error('Error in renderMaterialPreview:', e);
          resolve('');
        }
      });
    });
  }

  /**
   * Reset the preview counter and dispose the rig.
   */
  reset(): void {
    this.previewQueue = Promise.resolve();
    this.disposeRig();
  }

  /**
   * Clear the preview cache.
   */
  clearCache(): void {
    const cacheSize = this.previewCache.size;
    this.previewCache.clear();
    this.cacheHits = 0;
    this.cacheMisses = 0;
    Logger.log(`[PreviewRenderer] Cache cleared (${cacheSize} entries removed)`);
  }

  /**
   * Invalidate cache entries for a specific object and slot.
   */
  invalidateSlotCache(objectName: string, slotName: string): void {
    let removedCount = 0;
    for (const [key] of this.previewCache.entries()) {
      try {
        const cacheKey = JSON.parse(key) as MaterialCacheKey & { objectName?: string; slotName?: string };
        if (cacheKey.objectName === objectName && cacheKey.slotName === slotName) {
          this.previewCache.delete(key);
          removedCount++;
        }
      } catch {
        // Invalid JSON in cache key, skip
      }
    }
    if (removedCount > 0) {
      Logger.debug(`[PreviewRenderer] Invalidated ${removedCount} cache entries for object '${objectName}', slot '${slotName}'`);
    }
  }

  /**
   * Invalidate cache entries for a specific object (all slots).
   */
  invalidateObjectCache(objectName: string): void {
    let removedCount = 0;
    for (const [key] of this.previewCache.entries()) {
      try {
        const cacheKey = JSON.parse(key) as MaterialCacheKey & { objectName?: string };
        if (cacheKey.objectName === objectName) {
          this.previewCache.delete(key);
          removedCount++;
        }
      } catch {
        // Invalid JSON in cache key, skip
      }
    }
    if (removedCount > 0) {
      Logger.debug(`[PreviewRenderer] Invalidated ${removedCount} cache entries for object '${objectName}'`);
    }
  }

  /**
   * Get cache statistics.
   */
  getCacheStats(): { size: number; hits: number; misses: number; hitRate: number } {
    const total = this.cacheHits + this.cacheMisses;
    const hitRate = total > 0 ? (this.cacheHits / total) * 100 : 0;
    return {
      size: this.previewCache.size,
      hits: this.cacheHits,
      misses: this.cacheMisses,
      hitRate: Math.round(hitRate * 100) / 100,
    };
  }

  /**
   * Clean up any stray preview objects from a scene.
   */
  cleanupPreviewObjects(scene: Scene): void {
    const meshesToRemove = scene.meshes.filter(
      (m) => m.name === '__preview_sphere__'
    );
    const camerasToRemove = scene.cameras.filter(
      (c) => c.name === '__preview_cam__'
    );

    meshesToRemove.forEach((mesh) => mesh.dispose());
    camerasToRemove.forEach((camera) => camera.dispose());
    this.disposeRig();
  }
}
